/**
 * Geometry shared by the widget hosts. On desktop these drive OS window
 * sizes — keep in sync with mem-client-desktop's
 * src/modules/floating-agent/constants.ts. On web the same numbers size the
 * in-page overlay container.
 */
/* 96px window around the 64px circle: the ring keeps its 80px hackweek box
   (RING_BOX_PX), and the extra margin stops the sweep glow and the ×1.2
   release burst (~88px) from hard-clipping at the window edge. */
export const CIRCLE_WINDOW_SIZE_PX = 96;
export const CIRCLE_DIAMETER_PX = 64;
export const RING_BOX_PX = 80;
export const MOBILE_WEB_WIDGET_HOST_INSET_PX = 0;
export const WEB_WIDGET_HOST_INSET_PX = 16;
export const MOBILE_WEB_WIDGET_STACK_GAP_PX = 2;
export const WEB_WIDGET_STACK_GAP_PX = 6;
export const PANEL_WIDTH_PX = 400;
export const PANEL_HEIGHT_PX = 540;
/** Transparent host-window space that lets the panel shadow fade out naturally. */
export const PANEL_SHADOW_GUTTER_PX = 32;
/* 8px padding + 40px control row + 8px padding, per the ios-navbar-editor
   mock. PANEL_HEADER_GAP_PX is the clear space it leaves before the chat. */
export const PANEL_HEADER_HEIGHT_PX = 56;
export const PANEL_HEADER_GAP_PX = 4;
/* PTT-discovery hint under the composer: 16px row + 16px bottom inset,
   minus 12px pulled back from the composer section's own 20px bottom
   padding so the visible gap above the hint is the mock's 8px. */
export const PANEL_COMPOSER_HINT_HEIGHT_PX = 20;
/* Main↔thread horizontal slide duration. The thread composer's autofocus waits
   this long so it doesn't fire mid-transform (which strands the list partway). */
export const PANEL_THREAD_SLIDE_MS = 240;
export const PEEK_WINDOW_WIDTH_PX = 320;
/* Per-bubble slot: 3-line card + gap (+ tail on the circle-adjacent one).
   The window is sized to the visible stack — oversizing leaves an invisible
   window rectangle that blocks clicks on whatever is underneath. */
export const PEEK_BUBBLE_SLOT_PX = 124;
/** Fixed notice card plus the stack gap beneath/above it. */
export const VOICE_SESSION_NOTICE_SLOT_PX = 82;
export const MAX_VISIBLE_PEEK_BUBBLES = 3;
/* One ding per burst: the operator's multi-message turns land within a few
   seconds, so arrivals inside this window stay silent. */
export const PEEK_SOUND_COOLDOWN_MS = 3000;

/* A continuous spin for the whole response reads as noise. */
export const CIRCLE_THINKING_REPLAY_COOLDOWN_MS = 120_000;
/* Three cycles of the 2s loop in loading-arrows-tomato.json. */
export const CIRCLE_THINKING_ANIMATION_MS = 6000;

export const peekWindowHeightPx = (bubbleCount: number): number =>
  CIRCLE_WINDOW_SIZE_PX + 16 + bubbleCount * PEEK_BUBBLE_SLOT_PX;

export const floatingStackWindowHeightPx = ({
  bubbleCount,
  hasVoiceSessionNotice,
  expandedBubbleHeightPx = null,
}: {
  bubbleCount: number;
  hasVoiceSessionNotice: boolean;
  /**
   * Height the hover-expanded bubble is asking for, if one is expanded. It
   * replaces that bubble's fixed slot; every other bubble keeps its clamped
   * slot, so the stack grows by exactly the expansion's overflow.
   */
  expandedBubbleHeightPx?: number | null;
}): number => {
  const collapsedStackHeightPx =
    peekWindowHeightPx(bubbleCount) + (hasVoiceSessionNotice ? VOICE_SESSION_NOTICE_SLOT_PX : 0);

  if (expandedBubbleHeightPx === null) return collapsedStackHeightPx;

  const expansionOverflowPx = Math.max(expandedBubbleHeightPx - PEEK_BUBBLE_SLOT_PX, 0);
  return collapsedStackHeightPx + expansionOverflowPx;
};

/**
 * Breathing room kept between a fully expanded bubble and the far edge it grows
 * toward. Without it the bubble runs flush to the top of the screen (or tab)
 * and its rounded corners read as clipped against the edge.
 */
export const EXPANDED_PEEK_BUBBLE_EDGE_MARGIN_PX = 24;

/**
 * How tall a hover-expanded bubble may grow before it has to scroll instead.
 *
 * The budget is the work area (a display on desktop, the viewport on web) minus
 * everything the expanded bubble has to share the stack with: the circle, the
 * voice notice, the sibling bubbles still showing their clamped previews, and a
 * margin at the far edge. Without this a long reply produces a window taller
 * than the screen, whose top — where the message starts — is positioned
 * off-screen and unreachable.
 */
export const maxExpandedPeekBubbleHeightPx = ({
  availableHeightPx,
  bubbleCount,
  hasVoiceSessionNotice,
}: {
  availableHeightPx: number;
  bubbleCount: number;
  hasVoiceSessionNotice: boolean;
}): number => {
  const siblingBubbleCount = Math.max(bubbleCount - 1, 0);
  const reservedHeightPx =
    floatingStackWindowHeightPx({
      bubbleCount: siblingBubbleCount,
      hasVoiceSessionNotice,
    }) + EXPANDED_PEEK_BUBBLE_EDGE_MARGIN_PX;

  // A stack that already fills the screen still leaves the expanded bubble its
  // collapsed slot; it scrolls in place rather than collapsing to nothing.
  return Math.max(availableHeightPx - reservedHeightPx, PEEK_BUBBLE_SLOT_PX);
};

/** Pointer movement below this is a click; beyond it, a drag. */
export const CLICK_VS_DRAG_THRESHOLD_PX = 4;
export const CLICK_MAX_DURATION_MS = 220;

/**
 * Where a peek-bubble tap lands: the previewed message. A thread reply opens
 * its thread pane; a main-list reply scrolls the conversation to the message.
 */
export interface FloatingAgentPanelOpenTarget {
  eventId: string;
  threadRootId: string | null;
}

/**
 * Where a panel open should land. A tapped peek bubble names its own message;
 * every other entry point (circle, summon, PTT, in-app request) opens the
 * "default way" and lands on the first unread, falling back to the bottom.
 *
 * Callers must resolve this BEFORE marking replies seen: that write advances
 * the watermark optimistically, which would empty the unread set in the same
 * tick and always resolve to the bottom.
 */
export const resolveFloatingAgentOpenTarget = (
  floatingAgentPage: { firstUnreadRootEvent: { id: string } | null },
  explicitTarget?: FloatingAgentPanelOpenTarget
): FloatingAgentPanelOpenTarget | null => {
  // Shape-checked rather than truthy: the hosts pass expandToPanel straight to
  // onExpand props, so a caller wired to a DOM handler would hand us a click
  // event and silently suppress the first-unread jump.
  if (typeof explicitTarget?.eventId === "string") return explicitTarget;
  const firstUnread = floatingAgentPage.firstUnreadRootEvent;
  return firstUnread ? { eventId: firstUnread.id, threadRootId: null } : null;
};
